import React, { useState } from 'react'
import { BtnSize, BtnType, Button } from 'components/common/button'
import { useScoutMyRequest, useStartupPositionAnalysis } from 'apis/hooks'
import { getProfileCompleteStatus } from 'utils/formatters'
import { useStores } from 'stores/globals'
import { NewLoadingSpinner } from 'components/common/loading-spinner'
import { theme } from 'infra/theme'
import { Row } from 'components/common/layout'
import { observer } from 'mobx-react'
import { StartupPositionForAnalysisParams } from 'stores/position-analysis'
import { useIsDesktopCSR, useIsLoggedIn, useIsLoggedInGuest } from 'utils/hooks'
import { useRouter } from 'next/router'
import { analytics } from 'infra/analytics'
import { postGuestTokenCookie } from 'apis/request'
import { AnalysisStatusCode } from 'types/common'
import { withLoadingCheck } from 'utils/handlers'
import { getShouldGuideAnalyzeStorage } from 'utils/storages'
import { SHOULD_GUIDE_ANALYZE_KEY } from 'infra/constants'
import styled from 'styled-components'

// 버튼 텍스트 결정 함수
const getButtonText = (isAbleButton: boolean, isLoading: boolean) => {
  if (isAbleButton) return '서류 합격 가능성 분석하기'
  if (isLoading) return '분석 중...'
  return '서류 합격 가능성 분석 결과 보기'
}

export const PositionAnalyzeButton: React.FC<{
  position: StartupPositionForAnalysisParams
}> = observer(({ position }) => {
  const isLoggedIn = useIsLoggedIn()
  return isLoggedIn ? (
    <UserAnalyzeButton position={position} />
  ) : (
    <GuestAnalyzeButton position={position} />
  )
})

const GuestAnalyzeButton: React.FC<{
  position: StartupPositionForAnalysisParams
}> = observer(({ position }) => {
  const router = useRouter()
  const isDesktop = useIsDesktopCSR()
  const isGuest = useIsLoggedInGuest()
  const { positionAnalysisStore } = useStores()
  const { status: analysisStatus } = useStartupPositionAnalysis(position.id)

  const isLoading =
    !!positionAnalysisStore.analysisList[position.id]?.isLoading ||
    analysisStatus === AnalysisStatusCode.IN_PROGRESS
  const isAbleButton =
    (analysisStatus === undefined ||
      analysisStatus === AnalysisStatusCode.AVAILABLE ||
      analysisStatus === AnalysisStatusCode.FAILED) &&
    !isLoading

  const handleClick = async () => {
    analytics.track('click_position_analyze_button_by_non_member')
    if (getShouldGuideAnalyzeStorage())
      localStorage.removeItem(SHOULD_GUIDE_ANALYZE_KEY)
    if (isLoading) return
    if (analysisStatus === AnalysisStatusCode.COMPLETED) {
      const url = `/positions/${position.id}/analysis`
      isDesktop ? window.open(url, '_blank') : router.push(url)
    } else if (positionAnalysisStore.guestAnalysisCount >= 1) {
      positionAnalysisStore.openAnalysisModalForSignup()
    } else {
      !isGuest && (await postGuestTokenCookie()) // 게스트 토큰이 있는지 확인해서 토큰 발급
      positionAnalysisStore.openAnalysisModal(position, 'upload')
    }
  }

  return (
    <LoadingButton
      onClick={handleClick}
      isAbleButton={isAbleButton}
      isLoading={isLoading}
    >
      {getButtonText(isAbleButton, isLoading)}
    </LoadingButton>
  )
})

const UserAnalyzeButton: React.FC<{
  position: StartupPositionForAnalysisParams
}> = observer(({ position }) => {
  const router = useRouter()
  const isDesktop = useIsDesktopCSR()
  const { data: profile } = useScoutMyRequest()
  const { status: analysisStatus } = useStartupPositionAnalysis(position.id)
  const { positionAnalysisStore } = useStores()
  const { isAbleToAnalyze } = getProfileCompleteStatus(profile)
  const [isFetching, setIsFetching] = useState(false)

  if (analysisStatus === undefined) return null

  const isLoading =
    !!positionAnalysisStore.analysisList[position.id]?.isLoading ||
    analysisStatus === AnalysisStatusCode.IN_PROGRESS
  const isAbleButton =
    (analysisStatus === AnalysisStatusCode.AVAILABLE ||
      analysisStatus === AnalysisStatusCode.FAILED) &&
    !isLoading

  const handleClick = async () => {
    if (getShouldGuideAnalyzeStorage())
      localStorage.removeItem(SHOULD_GUIDE_ANALYZE_KEY)
    if (isLoading) return
    if (Object.keys(positionAnalysisStore.analysisIntervals).length >= 5) {
      return alert(
        '현재 분석 요청이 너무 많아서\n분석 결과가 나온 후 다시 시도해주세요.',
      )
    }
    const url = `/positions/${position.id}/analysis`
    if (analysisStatus === AnalysisStatusCode.COMPLETED) {
      // 분석 결과 있을 경우
      isDesktop ? window.open(url, '_blank') : router.push(url)
    } else if (isAbleToAnalyze) {
      // 완성된 프로필이 있는 경우
      try {
        await positionAnalysisStore.analyze(position)
        isDesktop && window.open(url, '_blank')
      } catch (e) {
        alert(e)
      }
    } else {
      // 프로필이 완성되지 않은 경우
      positionAnalysisStore.openAnalysisModal(position)
    }
    analytics.track('click_position_analyze_button', { analysisStatus })
  }

  return (
    <LoadingButton
      onClick={() => withLoadingCheck(isFetching, setIsFetching, handleClick)}
      isAbleButton={isAbleButton}
      isLoading={isLoading}
    >
      {getButtonText(isAbleButton, isLoading)}
    </LoadingButton>
  )
})

const LoadingButton: React.FCC<{
  onClick: () => void
  isAbleButton: boolean
  isLoading: boolean
}> = ({ onClick, isAbleButton, isLoading, children }) => {
  const isAnalyzeMode = getShouldGuideAnalyzeStorage()
  return (
    <EmphasizedButton
      size={BtnSize.SMALL}
      variant={isAbleButton ? BtnType.SECONDARY : BtnType.SECONDARY_OUTLINE}
      onClick={onClick}
      isEmphasized={isAbleButton && isAnalyzeMode}
    >
      <Row style={{ gap: 8, justifyContent: 'center' }}>
        {children}
        {isLoading && (
          <NewLoadingSpinner
            size={12}
            color={theme.color.secondary}
            strokeWidth={2}
          />
        )}
      </Row>
    </EmphasizedButton>
  )
}

const EmphasizedButton = styled(Button)<{ isEmphasized: boolean }>`
  ${(p) =>
    p.isEmphasized &&
    `
    animation: shake 0.8s 2;
    @keyframes shake {
      0% { transform: translateX(0); }
      20% { transform: translateX(-4px); }
      40% { transform: translateX(4px); }
      60% { transform: translateX(-4px); }
      80% { transform: translateX(4px); }
      100% { transform: translateX(0); }
    }
  `}
`
